'use client';

import Link from "next/link";
import Image from "next/image";
import { Button } from "./ui/button";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import LocaleSwitcher from "./locale-switcher";
import { useTranslations } from 'next-intl';
import { useParams, usePathname } from 'next/navigation';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const t = useTranslations('Navbar');
  const params = useParams();
  const pathname = usePathname();
  
  // Get the current locale from the URL
  const currentLocale = params?.locale || 'en';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Ensure we have translations loaded from global space
    if (typeof window !== 'undefined' && !window.__NEXT_INTL_MESSAGES__) {
      console.warn('Translation messages not found in window.__NEXT_INTL_MESSAGES__');
    }
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Prevent scrolling when menu is open
    document.body.style.overflow = isMenuOpen ? 'unset' : 'hidden';
  };

  // Helper function to create localized URLs
  const localizedUrl = (path: string) => {
    if (path.startsWith('/')) {
      return `/${currentLocale}${path}`;
    }
    return `/${currentLocale}/${path}`;
  };

  // Include the locale in navigation links
  const navLinks = [
    { href: localizedUrl('/services'), label: t('services') },
    { href: localizedUrl('/industries'), label: t('industries') },
    { href: localizedUrl('/portfolio'), label: t('portfolio') },
    { href: localizedUrl('/blog'), label: t('blog') },
    { href: localizedUrl('/about'), label: t('about') },
    // Careers page hidden as it's a future implementation
    // { href: localizedUrl('/careers'), label: t('careers') },
  ];

  return (
    <>
      <div className="fixed top-0 w-full z-50">
        <div className="flex justify-between items-center pt-4 px-4">
          {/* Logo Section - Separate Floating Element */}
          <div className="bg-white/90 backdrop-blur-md rounded-full shadow-lg border border-white/20 h-14">
            <Link href={localizedUrl('/')} className="flex items-center gap-2 font-bold text-xl bg-white px-6 py-3 rounded-full h-full">
              <Image 
                src="/icons/openlabslogo.ico" 
                alt="OpenLabs Research Logo" 
                width={32} 
                height={32} 
                className="w-8 h-8 object-contain"
              />
              OpenLabs Research
            </Link>
          </div>
          
          {/* Main Navigation - Separate Floating Element */}
          <nav className="flex items-center bg-white/90 backdrop-blur-md rounded-full shadow-lg border border-white/20 overflow-hidden h-14">
            {/* Navigation Section - Desktop */}
            <div className="hidden lg:flex items-center px-2">
              {navLinks.map((link, index) => (
                <Link 
                  key={link.href} 
                  href={link.href}
                  className="px-4 py-3 text-xl font-bold transition hover:text-primary hover:bg-white/30 rounded-md mx-1 h-full flex items-center"
                >
                  {link.label}
                </Link>
              ))}
            </div>
            
            {/* Right Section - Social Icons and Contact */}
            <div className="hidden md:flex items-center gap-3 px-6 h-full">
              <Link 
                href="https://twitter.com/openlabsresearch" 
                target="_blank" 
                className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-primary transition-colors hover:bg-white/30 rounded-full"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </Link>
              <Link 
                href="https://www.instagram.com/openlabsresearch?igsh=MThydWg3bW81OWNiZQ%3D%3D&utm_source=qr" 
                target="_blank" 
                className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-primary transition-colors hover:bg-white/30 rounded-full"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16.98 0a6.9 6.9 0 0 1 5.08 1.98A6.94 6.94 0 0 1 24 7.02v9.96c0 2.08-.68 3.87-1.98 5.13A7.14 7.14 0 0 1 16.94 24H7.06a7.06 7.06 0 0 1-5.03-1.89A6.96 6.96 0 0 1 0 16.94V7.02C0 2.8 2.8 0 7.02 0h9.96zm.05 2.23H7.06c-1.45 0-2.7.43-3.53 1.25a4.82 4.82 0 0 0-1.3 3.54v9.92c0 1.5.43 2.7 1.3 3.58a5 5 0 0 0 3.53 1.25h9.88a5 5 0 0 0 3.53-1.25 4.73 4.73 0 0 0 1.4-3.54V7.02a5 5 0 0 0-1.3-3.49 4.82 4.82 0 0 0-3.54-1.3zM12 5.76c3.39 0 6.2 2.8 6.2 6.2a6.2 6.2 0 0 1-12.4 0 6.2 6.2 0 0 1 6.2-6.2zm0 2.22a3.99 3.99 0 0 0-3.97 3.97A3.99 3.99 0 0 0 12 15.92a3.99 3.99 0 0 0 3.97-3.97A3.99 3.99 0 0 0 12 7.98zm6.44-3.77a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z"/>
                </svg>
              </Link>
              <Link 
                href="https://linkedin.com/company/openlabsresearch" 
                target="_blank" 
                className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-primary transition-colors hover:bg-white/30 rounded-full"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </Link>
              <div className="mx-2">
                <LocaleSwitcher />
              </div>
              <Button variant="outline" size="sm" className="rounded-full bg-white/50 border-white/30 hover:bg-white font-bold text-xl" asChild>
                <Link href={localizedUrl('/contact')}>{t('contact')}</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden px-4 py-3 focus:outline-none h-full flex items-center justify-center" 
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-0 bg-black/50 backdrop-blur-sm z-40 pt-20">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl mx-4 mt-4 p-6 shadow-xl">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link 
                  key={link.href} 
                  href={link.href}
                  className="text-lg py-3 px-4 rounded-xl bg-gray-50 hover:bg-gray-100 text-center font-medium transition"
                  onClick={() => {
                    setIsMenuOpen(false);
                    document.body.style.overflow = 'unset';
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="mt-6 space-y-4">
              <div className="flex justify-center gap-4">
                <Link 
                  href="https://twitter.com/openlabsresearch" 
                  target="_blank" 
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full text-gray-600 hover:text-primary transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </Link>
                <Link 
                  href="https://www.instagram.com/openlabsresearch?igsh=MThydWg3bW81OWNiZQ%3D%3D&utm_source=qr" 
                  target="_blank" 
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full text-gray-600 hover:text-primary transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16.98 0a6.9 6.9 0 0 1 5.08 1.98A6.94 6.94 0 0 1 24 7.02v9.96c0 2.08-.68 3.87-1.98 5.13A7.14 7.14 0 0 1 16.94 24H7.06a7.06 7.06 0 0 1-5.03-1.89A6.96 6.96 0 0 1 0 16.94V7.02C0 2.8 2.8 0 7.02 0h9.96zm.05 2.23H7.06c-1.45 0-2.7.43-3.53 1.25a4.82 4.82 0 0 0-1.3 3.54v9.92c0 1.5.43 2.7 1.3 3.58a5 5 0 0 0 3.53 1.25h9.88a5 5 0 0 0 3.53-1.25 4.73 4.73 0 0 0 1.4-3.54V7.02a5 5 0 0 0-1.3-3.49 4.82 4.82 0 0 0-3.54-1.3zM12 5.76c3.39 0 6.2 2.8 6.2 6.2a6.2 6.2 0 0 1-12.4 0 6.2 6.2 0 0 1 6.2-6.2zm0 2.22a3.99 3.99 0 0 0-3.97 3.97A3.99 3.99 0 0 0 12 15.92a3.99 3.99 0 0 0 3.97-3.97A3.99 3.99 0 0 0 12 7.98zm6.44-3.77a1.4 1.4 0 1 1 0 2.8 1.4 1.4 0 0 1 0-2.8z"/>
                  </svg>
                </Link>
                <Link 
                  href="https://linkedin.com/company/openlabsresearch" 
                  target="_blank" 
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full text-gray-600 hover:text-primary transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </Link>
              </div>
              <div className="flex flex-col gap-3">
                <LocaleSwitcher />
              </div>
              <Button size="lg" className="w-full rounded-full font-bold text-lg" asChild>
                <Link href={localizedUrl('/contact')}>{t('contact')}</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}